import { CodeBlock } from "@/components/CodeBlock";
import MarketingLayout from "@/components/MarketingLayout";
import { TabbedCodeBlock } from "@/components/TabbedCodeBlock";
import { Badge } from "@/components/ui/badge";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  buildSnippets,
  secretSnippets,
  proxySnippets,
  virtualEnvSnippets,
} from "@/snippets";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Clock,
  Code,
  ExternalLink,
  FolderKey,
  Key,
  Network,
  Package,
  Search,
  Shield,
  Terminal,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";

const DOC_SECTION_KEYS = [
  "getting-started",
  "secrets",
  "proxy",
  "virtual-env",
  "cli-reauth",
  "claude-desktop",
  "claude-code",
  "cursor",
] as const;

type DocsSectionKey = (typeof DOC_SECTION_KEYS)[number];

function getSectionFromHash(hash: string): DocsSectionKey {
  const normalizedHash = hash.replace("#", "") as DocsSectionKey;
  return DOC_SECTION_KEYS.includes(normalizedHash)
    ? normalizedHash
    : "getting-started";
}

function NavItem({
  section,
  active,
  label,
  onClick,
}: {
  section: DocsSectionKey;
  active: DocsSectionKey;
  label: string;
  onClick: (s: DocsSectionKey) => void;
}) {
  return (
    <li>
      <button
        onClick={() => onClick(section)}
        className={`w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
          active === section
            ? "bg-primary/10 text-primary font-medium"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
        }`}
      >
        {label}
      </button>
    </li>
  );
}

function StepCard({
  step,
  title,
  description,
  children,
}: {
  step: number;
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex gap-2 sm:gap-4">
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
        {step}
      </div>
      <div className="flex-1 min-w-0 pb-8 border-l border-border pl-3 sm:pl-6 ml-2 sm:ml-5 -mt-2">
        <h3 className="text-base font-semibold mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground mb-4">{description}</p>
        {children}
      </div>
    </div>
  );
}

const NAV_ITEMS: Array<{ key: DocsSectionKey; label: string }> = [
  { key: "getting-started", label: "Quick Start" },
  { key: "secrets", label: "Secrets" },
  { key: "proxy", label: "Proxy Pass" },
  { key: "virtual-env", label: "Virtual .env" },
  { key: "cli-reauth", label: "CLI Re-auth" },
  { key: "claude-desktop", label: "Claude Desktop" },
  { key: "claude-code", label: "Claude Code" },
  { key: "cursor", label: "Cursor" },
];

interface SearchEntry {
  section: DocsSectionKey;
  sectionLabel: string;
  title: string;
  keywords: string;
}

const SEARCH_INDEX: SearchEntry[] = [
  // Quick Start
  { section: "getting-started", sectionLabel: "Quick Start", title: "Installation", keywords: "pip install double-o python package async retry all extras" },
  { section: "getting-started", sectionLabel: "Quick Start", title: "Create a Secret", keywords: "new secret name api key aes-256-gcm encryption first step" },
  { section: "getting-started", sectionLabel: "Quick Start", title: "Generate a Token", keywords: "token ttl 1 hour day 7 30 short-lived access generate" },
  { section: "getting-started", sectionLabel: "Quick Start", title: "Fetch in Your Code", keywords: "sdk fetch secret value authorization bearer header python javascript curl r" },
  { section: "getting-started", sectionLabel: "Quick Start", title: "Short-Lived Tokens", keywords: "expire ttl leaked useless security" },
  { section: "getting-started", sectionLabel: "Quick Start", title: "IP Whitelisting", keywords: "restrict ip address trusted location whitelist" },
  { section: "getting-started", sectionLabel: "Quick Start", title: "Encrypted Storage", keywords: "aes-256-gcm encryption rest salt secure" },
  { section: "getting-started", sectionLabel: "Quick Start", title: "Audit Logs", keywords: "log timestamp ip access who what when visibility" },
  { section: "getting-started", sectionLabel: "Quick Start", title: "SDK Quick Reference", keywords: "fetch secret virtual env proxy python javascript curl r pypi" },
  // Secrets
  { section: "secrets", sectionLabel: "Secrets", title: "Creating a Secret", keywords: "new secret name api key credential paste value sidebar" },
  { section: "secrets", sectionLabel: "Secrets", title: "Generating Access Tokens", keywords: "token short-lived notebook embed bridge code" },
  { section: "secrets", sectionLabel: "Secrets", title: "IP Whitelisting", keywords: "ip whitelist security tab add find my ip office home vpn aws lambda" },
  { section: "secrets", sectionLabel: "Secrets", title: "Revoking a Secret", keywords: "revoke compromise immediately tokens stop working" },
  // Proxy Pass
  { section: "proxy", sectionLabel: "Proxy Pass", title: "How Proxy Pass Works", keywords: "forward inject real api key temp token openai aws stripe automatic" },
  { section: "proxy", sectionLabel: "Proxy Pass", title: "Setting Up a Proxy", keywords: "new proxy target url select secret authentication type" },
  { section: "proxy", sectionLabel: "Proxy Pass", title: "Using the Proxy", keywords: "proxy token request code sdk never see actual key" },
  { section: "proxy", sectionLabel: "Proxy Pass", title: "Bearer Token Auth", keywords: "authorization bearer header bearer token api key" },
  { section: "proxy", sectionLabel: "Proxy Pass", title: "API Key Header Auth", keywords: "x-api-key header custom authentication type" },
  { section: "proxy", sectionLabel: "Proxy Pass", title: "Basic Auth", keywords: "authorization basic base64 encoded authentication" },
  // Virtual .env
  { section: "virtual-env", sectionLabel: "Virtual .env", title: "What is Virtual .env?", keywords: "group related secrets single token env file openai stripe database" },
  { section: "virtual-env", sectionLabel: "Virtual .env", title: "Creating a Virtual .env", keywords: "new virtual env name production development add secrets key value" },
  { section: "virtual-env", sectionLabel: "Virtual .env", title: "Using Virtual .env", keywords: "load env variables single line recommended oo load_env" },
  { section: "virtual-env", sectionLabel: "Virtual .env", title: "Jupyter Notebooks", keywords: "jupyter notebook load secrets start cell" },
  { section: "virtual-env", sectionLabel: "Virtual .env", title: "Environment Parity", keywords: "dev staging prod same secrets environment parity" },
  { section: "virtual-env", sectionLabel: "Virtual .env", title: "Team Collaboration", keywords: "share secret groups team without sharing values" },
  // CLI Re-auth
  { section: "cli-reauth", sectionLabel: "CLI Re-auth", title: "How Re-auth Works", keywords: "expired token 401 reauth_url script cli prompt renew" },
  { section: "cli-reauth", sectionLabel: "CLI Re-auth", title: "401 Response Format", keywords: "http 401 error token expired reauth_url json response" },
  { section: "cli-reauth", sectionLabel: "CLI Re-auth", title: "Python Re-auth Example", keywords: "python requests pathlib load save token expired error reauth loop" },
  { section: "cli-reauth", sectionLabel: "CLI Re-auth", title: "cURL Example", keywords: "curl bash http_code 401 response reauth_url shell" },
  { section: "cli-reauth", sectionLabel: "CLI Re-auth", title: "Status Code Reference", keywords: "200 401 403 404 429 status code meaning action rate limit forbidden invalid" },
  // Claude Desktop
  { section: "claude-desktop", sectionLabel: "Claude Desktop", title: "MCP Token Setup", keywords: "mcp token settings mcp access bearer scoped cannot read direct" },
  { section: "claude-desktop", sectionLabel: "Claude Desktop", title: "Claude Desktop Config", keywords: "claude_desktop_config.json macos mcpservers double-o http url headers" },
  { section: "claude-desktop", sectionLabel: "Claude Desktop", title: "Use Secrets in Claude Desktop", keywords: "restart claude desktop mcp tool fetch secret name openai api key" },
  // Claude Code
  { section: "claude-code", sectionLabel: "Claude Code", title: "MCP Token Setup", keywords: "mcp token settings mcp access bearer scoped" },
  { section: "claude-code", sectionLabel: "Claude Code", title: "Add MCP Server via CLI", keywords: "claude mcp add terminal command header authorization bearer" },
  { section: "claude-code", sectionLabel: "Claude Code", title: "Add MCP Server via .mcp.json", keywords: "mcp.json project root mcpservers http url headers" },
  { section: "claude-code", sectionLabel: "Claude Code", title: "Use Secrets in Claude Code", keywords: "stripe secret key coding session no api keys codebase" },
  // Cursor
  { section: "cursor", sectionLabel: "Cursor", title: "MCP Token Setup", keywords: "mcp token settings mcp access bearer scoped" },
  { section: "cursor", sectionLabel: "Cursor", title: "Add MCP Server in Cursor", keywords: "cursor settings features mcp add server mcp.json project root" },
  { section: "cursor", sectionLabel: "Cursor", title: "Use Secrets in Cursor AI Chat", keywords: "cmd+l ai chat database url migration script securely fetch" },
];

export default function Docs() {
  const [activeSection, setActiveSection] = useState<DocsSectionKey>(() => {
    if (typeof window === "undefined") return "getting-started";
    return getSectionFromHash(window.location.hash);
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const syncSectionFromHash = () => {
      setActiveSection(getSectionFromHash(window.location.hash));
    };

    syncSectionFromHash();
    window.addEventListener("hashchange", syncSectionFromHash);
    return () => window.removeEventListener("hashchange", syncSectionFromHash);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(open => !open);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  function navigate(section: DocsSectionKey) {
    setActiveSection(section);
    window.location.hash = section;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleSearchSelect(section: DocsSectionKey) {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(section);
  }

  const searchResults = searchQuery.trim()
    ? SEARCH_INDEX.filter(entry =>
        (entry.title + " " + entry.keywords + " " + entry.sectionLabel)
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim())
      )
    : [];

  // Group results by sectionLabel preserving first-seen order
  const groupedResults = searchResults.reduce<
    Array<{ sectionLabel: string; entries: SearchEntry[] }>
  >((acc, entry) => {
    const group = acc.find(g => g.sectionLabel === entry.sectionLabel);
    if (group) {
      group.entries.push(entry);
    } else {
      acc.push({ sectionLabel: entry.sectionLabel, entries: [entry] });
    }
    return acc;
  }, []);

  const isMac = typeof navigator !== "undefined" && /Mac/.test(navigator.platform);

  return (
    <MarketingLayout>
      <CommandDialog
        open={searchOpen}
        onOpenChange={open => {
          setSearchOpen(open);
          if (!open) setSearchQuery("");
        }}
        title="Search documentation"
        description="Search across all docs sections"
      >
        <CommandInput
          placeholder="Search documentation..."
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <CommandList>
          {searchQuery.trim() && groupedResults.length === 0 && (
            <CommandEmpty>No results for "{searchQuery}"</CommandEmpty>
          )}
          {!searchQuery.trim() && (
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
              Type to search across all docs…
            </CommandEmpty>
          )}
          {groupedResults.map(({ sectionLabel, entries }) => (
            <CommandGroup key={sectionLabel} heading={sectionLabel}>
              {entries.map(entry => (
                <CommandItem
                  key={`${entry.section}-${entry.title}`}
                  onSelect={() => handleSearchSelect(entry.section)}
                  className="cursor-pointer"
                >
                  <Search className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                  {entry.title}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>

      <div className="container py-10">
        <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-12 max-w-5xl mx-auto">

          {/* Sidebar — desktop */}
          <aside className="hidden lg:block">
            <nav className="sticky top-8 space-y-6">
              {/* Search trigger */}
              <button
                onClick={() => setSearchOpen(true)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md border bg-muted/30 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
              >
                <Search className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="flex-1 text-left">Search docs…</span>
                <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {isMac ? "⌘" : "Ctrl"}K
                </kbd>
              </button>

              <div>
                <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Guides
                </p>
                <ul className="space-y-0.5">
                  <NavItem section="getting-started" active={activeSection} label="Quick Start" onClick={navigate} />
                  <NavItem section="secrets" active={activeSection} label="Secrets" onClick={navigate} />
                  <NavItem section="proxy" active={activeSection} label="Proxy Pass" onClick={navigate} />
                  <NavItem section="virtual-env" active={activeSection} label="Virtual .env" onClick={navigate} />
                  <NavItem section="cli-reauth" active={activeSection} label="CLI Re-auth" onClick={navigate} />
                </ul>
              </div>
              <div>
                <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Integrations
                </p>
                <ul className="space-y-0.5">
                  <NavItem section="claude-desktop" active={activeSection} label="Claude Desktop" onClick={navigate} />
                  <NavItem section="claude-code" active={activeSection} label="Claude Code" onClick={navigate} />
                  <NavItem section="cursor" active={activeSection} label="Cursor" onClick={navigate} />
                </ul>
              </div>
            </nav>
          </aside>

          {/* Main content */}
          <main className="min-w-0">
            {/* Mobile search trigger */}
            <button
              onClick={() => setSearchOpen(true)}
              className="lg:hidden w-full flex items-center gap-2 px-3 py-2 mb-3 rounded-md border bg-muted/30 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
            >
              <Search className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="flex-1 text-left">Search docs…</span>
              <kbd className="inline-flex items-center gap-0.5 rounded border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                {isMac ? "⌘" : "Ctrl"}K
              </kbd>
            </button>

            {/* Mobile nav */}
            <div className="lg:hidden mb-6 -mx-4 px-4 overflow-x-auto">
              <div className="flex gap-1.5 pb-1 w-max">
                {NAV_ITEMS.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => navigate(key)}
                    className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                      activeSection === key
                        ? "bg-primary text-primary-foreground font-medium"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Getting Started ── */}
            {activeSection === "getting-started" && (
              <div className="space-y-10">
                <div className="pb-6 border-b">
                  <h1 className="text-2xl font-bold mb-2">Quick Start</h1>
                  <p className="text-muted-foreground">
                    Get up and running with Double-O in under 2 minutes.
                  </p>
                </div>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Installation</h2>
                  <CodeBlock code="pip install double-o" language="bash" />
                  <div className="grid md:grid-cols-3 gap-3">
                    <div className="p-3 border rounded-lg bg-muted/20">
                      <p className="text-xs font-medium mb-2">Async support</p>
                      <CodeBlock code='pip install "double-o[async]"' language="bash" />
                    </div>
                    <div className="p-3 border rounded-lg bg-muted/20">
                      <p className="text-xs font-medium mb-2">Retry support</p>
                      <CodeBlock code='pip install "double-o[retry]"' language="bash" />
                    </div>
                    <div className="p-3 border rounded-lg bg-muted/20">
                      <p className="text-xs font-medium mb-2">Everything</p>
                      <CodeBlock code='pip install "double-o[all]"' language="bash" />
                    </div>
                  </div>
                  <a
                    href="https://pypi.org/project/double-o/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                  >
                    <Package className="h-4 w-4" />
                    View on PyPI
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Your first secret in 3 steps</h2>
                  <StepCard
                    step={1}
                    title="Create a Secret"
                    description="Navigate to the Secrets page and click 'New Secret'. Enter a name and paste your API key or credential."
                  >
                    <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg text-sm">
                      <Key className="h-4 w-4 text-primary flex-shrink-0" />
                      <span>Your secret is encrypted with AES-256-GCM before storage</span>
                    </div>
                  </StepCard>
                  <StepCard
                    step={2}
                    title="Generate a Token"
                    description="Click on your secret, go to the 'Tokens' tab, and generate a short-lived access token. Choose a TTL that suits your use case."
                  >
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">1 hour</Badge>
                      <Badge variant="outline">6 hours</Badge>
                      <Badge variant="outline">1 day</Badge>
                      <Badge variant="outline">7 days</Badge>
                      <Badge variant="outline">30 days</Badge>
                    </div>
                  </StepCard>
                  <StepCard
                    step={3}
                    title="Fetch in Your Code"
                    description="Use the token in your code to fetch the secret value securely. The SDK sends tokens through Authorization headers."
                  >
                    <TabbedCodeBlock
                      snippets={buildSnippets(secretSnippets, "YOUR_TOKEN_HERE")}
                    />
                  </StepCard>
                  <div className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-green-700 dark:text-green-400">
                      That's it! Your secret is never exposed in your codebase.
                    </span>
                  </div>
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Key concepts</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold text-sm">Short-Lived Tokens</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Tokens automatically expire after their TTL. Even if leaked, they become useless quickly.
                      </p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold text-sm">IP Whitelisting</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Restrict access to specific IP addresses. Only trusted locations can fetch your secrets.
                      </p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold text-sm">Encrypted Storage</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        All secrets are encrypted at rest using AES-256-GCM with unique salts per secret.
                      </p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold text-sm">Audit Logs</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Every access is logged with timestamp and IP. Full visibility into who accessed what and when.
                      </p>
                    </div>
                  </div>
                </section>

                <section className="space-y-6">
                  <h2 className="text-base font-semibold">SDK quick reference</h2>
                  <div>
                    <h3 className="text-sm font-medium mb-2 text-muted-foreground">Fetching a secret</h3>
                    <TabbedCodeBlock snippets={buildSnippets(secretSnippets, "TOKEN")} />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium mb-2 text-muted-foreground">Loading Virtual .env</h3>
                    <TabbedCodeBlock snippets={buildSnippets(virtualEnvSnippets, "VIRTUAL_ENV_TOKEN")} />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium mb-2 text-muted-foreground">Making a proxy request</h3>
                    <TabbedCodeBlock snippets={buildSnippets(proxySnippets, "PROXY_TOKEN")} />
                  </div>
                  <a
                    href="https://pypi.org/project/double-o/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary hover:underline font-medium text-sm"
                  >
                    View full SDK documentation
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </section>
              </div>
            )}

            {/* ── Secrets ── */}
            {activeSection === "secrets" && (
              <div className="space-y-10">
                <div className="pb-6 border-b">
                  <h1 className="text-2xl font-bold mb-2">Secrets</h1>
                  <p className="text-muted-foreground">
                    Store API keys, credentials, and sensitive data securely.
                  </p>
                </div>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Creating a secret</h2>
                  <ol className="space-y-3 text-sm text-muted-foreground">
                    {[
                      <>Go to <strong className="text-foreground">Secrets</strong> in the sidebar</>,
                      <>Click <strong className="text-foreground">New Secret</strong></>,
                      <>Enter a descriptive name (e.g., "OpenAI API Key")</>,
                      <>Paste your secret value (API key, password, etc.)</>,
                      <>Optionally enable <strong className="text-foreground">Auto-whitelist First Request</strong> to automatically allow the first IP that accesses it</>,
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">
                          {i + 1}
                        </span>
                        <span className="pt-0.5">{item}</span>
                      </li>
                    ))}
                  </ol>
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Generating access tokens</h2>
                  <p className="text-sm text-muted-foreground">
                    Tokens are the bridge between your code and your secrets. They're designed to be
                    short-lived and can be safely embedded in notebooks.
                  </p>
                  <TabbedCodeBlock
                    snippets={buildSnippets(secretSnippets, "oo_abc123xyz...")}
                  />
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">IP whitelisting</h2>
                  <p className="text-sm text-muted-foreground">
                    For additional security, restrict which IP addresses can access your secrets
                    from the Security tab on any secret.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {[
                      "Click \"Add IP\" to whitelist specific addresses",
                      "Use \"Find My IP\" to detect your current IP",
                      "Select from saved IPs for quick reuse",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="pt-2">
                    <h3 className="text-sm font-semibold mb-3">Saved IP addresses</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      The <strong className="text-foreground">IP Addresses</strong> page lets you
                      save commonly used IPs with labels so you can quickly apply them to any
                      secret's whitelist.
                    </p>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="p-4 border rounded-lg text-center">
                        <Network className="h-8 w-8 text-primary mx-auto mb-2" />
                        <p className="text-sm font-medium">Office Network</p>
                        <p className="text-xs text-muted-foreground font-mono">203.0.113.50</p>
                      </div>
                      <div className="p-4 border rounded-lg text-center">
                        <Network className="h-8 w-8 text-primary mx-auto mb-2" />
                        <p className="text-sm font-medium">Home VPN</p>
                        <p className="text-xs text-muted-foreground font-mono">198.51.100.23</p>
                      </div>
                      <div className="p-4 border rounded-lg text-center">
                        <Network className="h-8 w-8 text-primary mx-auto mb-2" />
                        <p className="text-sm font-medium">AWS Lambda</p>
                        <p className="text-xs text-muted-foreground font-mono">52.94.76.0/24</p>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Revoking a secret</h2>
                  <p className="text-sm text-muted-foreground">
                    If a secret is compromised, you can instantly revoke it. All tokens will stop
                    working immediately and the secret cannot be accessed until you update it.
                  </p>
                </section>
              </div>
            )}

            {/* ── Proxy Pass ── */}
            {activeSection === "proxy" && (
              <div className="space-y-10">
                <div className="pb-6 border-b">
                  <h1 className="text-2xl font-bold mb-2">Proxy Pass</h1>
                  <p className="text-muted-foreground">
                    Forward API requests with automatic credential injection.
                  </p>
                </div>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">How it works</h2>
                  <p className="text-sm text-muted-foreground">
                    Instead of embedding your real API key in code, you embed a short-lived
                    proxy token. Double-O receives the request, swaps in your actual key, and
                    forwards it to the target API — your code never sees the real credential.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-sm text-muted-foreground py-6 bg-muted/30 rounded-lg">
                    <div className="px-3 py-2 bg-background border rounded-lg font-mono">Your Code</div>
                    <ArrowRight className="h-4 w-4 rotate-90 sm:rotate-0" />
                    <div className="px-3 py-2 bg-primary/10 border border-primary/20 rounded-lg font-mono text-primary">Temp Token</div>
                    <ArrowRight className="h-4 w-4 rotate-90 sm:rotate-0" />
                    <div className="px-3 py-2 bg-background border rounded-lg font-mono">Double-O</div>
                    <ArrowRight className="h-4 w-4 rotate-90 sm:rotate-0" />
                    <div className="px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg font-mono text-green-600">Real API Key</div>
                    <ArrowRight className="h-4 w-4 rotate-90 sm:rotate-0" />
                    <div className="px-3 py-2 bg-background border rounded-lg font-mono">OpenAI / AWS / etc</div>
                  </div>
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Setting up a proxy</h2>
                  <ol className="space-y-3 text-sm text-muted-foreground">
                    {[
                      <>First, create a secret with your API key (e.g., OpenAI API key)</>,
                      <>Go to <strong className="text-foreground">Proxy Pass</strong> → <strong className="text-foreground">New Proxy</strong></>,
                      <>Select the secret and enter the target URL (e.g., https://api.openai.com)</>,
                      <>Choose the authentication type (Bearer, API Key, Basic Auth, etc.)</>,
                      <>Generate a proxy token from the proxy detail page</>,
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">
                          {i + 1}
                        </span>
                        <span className="pt-0.5">{item}</span>
                      </li>
                    ))}
                  </ol>
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Using the proxy</h2>
                  <TabbedCodeBlock
                    snippets={buildSnippets(proxySnippets, "YOUR_PROXY_TOKEN")}
                  />
                  <p className="text-sm text-muted-foreground">
                    The proxy automatically injects your real API key into the request headers —
                    your code never sees the actual key.
                  </p>
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Authentication types</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-3 border rounded-lg">
                      <Badge className="mb-2">Bearer Token</Badge>
                      <p className="text-sm text-muted-foreground font-mono">Authorization: Bearer YOUR_API_KEY</p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <Badge className="mb-2">API Key Header</Badge>
                      <p className="text-sm text-muted-foreground font-mono">X-API-Key: YOUR_API_KEY</p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <Badge className="mb-2">Basic Auth</Badge>
                      <p className="text-sm text-muted-foreground font-mono">Authorization: Basic BASE64_ENCODED</p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <Badge className="mb-2">Custom</Badge>
                      <p className="text-sm text-muted-foreground">Define your own header name and prefix</p>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {/* ── Virtual .env ── */}
            {activeSection === "virtual-env" && (
              <div className="space-y-10">
                <div className="pb-6 border-b">
                  <h1 className="text-2xl font-bold mb-2">Virtual .env</h1>
                  <p className="text-muted-foreground">
                    Group multiple secrets under a single token for easier management.
                  </p>
                </div>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">What is Virtual .env?</h2>
                  <p className="text-sm text-muted-foreground">
                    Instead of managing separate tokens for each secret, you can group related
                    secrets together and access them all with a single token — just like a
                    traditional <code className="bg-muted px-1.5 py-0.5 rounded text-xs">.env</code> file, but stored securely in Double-O.
                  </p>
                  <div className="p-4 bg-muted/30 rounded-lg border">
                    <div className="flex items-center gap-2 mb-3">
                      <FolderKey className="h-5 w-5 text-primary" />
                      <span className="font-medium text-sm">Production API Keys</span>
                    </div>
                    <div className="space-y-1 text-sm font-mono text-muted-foreground pl-7">
                      <div>OPENAI_API_KEY=sk-••••••••</div>
                      <div>DATABASE_URL=postgresql://••••••••</div>
                      <div>STRIPE_SECRET_KEY=sk_live_••••••••</div>
                    </div>
                  </div>
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Creating a Virtual .env</h2>
                  <ol className="space-y-3 text-sm text-muted-foreground">
                    {[
                      <>Go to <strong className="text-foreground">Virtual .env</strong> → <strong className="text-foreground">New Virtual .env</strong></>,
                      <>Give it a name (e.g., "Production" or "Development")</>,
                      <>Add secrets with their environment variable names (KEY=value format)</>,
                      <>Generate a token to access all secrets at once</>,
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">
                          {i + 1}
                        </span>
                        <span className="pt-0.5">{item}</span>
                      </li>
                    ))}
                  </ol>
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Using Virtual .env</h2>
                  <TabbedCodeBlock
                    snippets={buildSnippets(virtualEnvSnippets, "YOUR_TOKEN_HERE")}
                  />
                  <p className="text-sm text-muted-foreground">
                    This is the recommended approach — it loads all secrets from your Virtual .env
                    directly into your environment variables with a single line.
                  </p>
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Use cases</h2>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {[
                      { label: "Jupyter Notebooks", desc: "Load all secrets at the start of your notebook" },
                      { label: "Environment Parity", desc: "Same secrets across dev, staging, and prod" },
                      { label: "Team Collaboration", desc: "Share secret groups without sharing values" },
                    ].map(({ label, desc }) => (
                      <li key={label} className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span>
                          <strong className="text-foreground">{label}</strong> — {desc}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            )}

            {/* ── CLI Re-auth ── */}
            {activeSection === "cli-reauth" && (
              <div className="space-y-10">
                <div className="pb-6 border-b">
                  <h1 className="text-2xl font-bold mb-2">CLI Re-authentication</h1>
                  <p className="text-muted-foreground">
                    Handle expired tokens gracefully in scripts and CLI tools.
                  </p>
                </div>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">How it works</h2>
                  <p className="text-sm text-muted-foreground">
                    When a token expires, the API returns{" "}
                    <code className="bg-muted px-1.5 py-0.5 rounded text-xs">HTTP 401</code> with a{" "}
                    <code className="bg-muted px-1.5 py-0.5 rounded text-xs">reauth_url</code> field.
                    Your script can use this URL to prompt the user to generate a new token without
                    navigating the full UI.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-sm text-muted-foreground py-6 bg-muted/30 rounded-lg">
                    <div className="px-3 py-2 bg-background border rounded-lg font-mono">Script</div>
                    <ArrowRight className="h-4 w-4 rotate-90 sm:rotate-0" />
                    <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg font-mono text-red-600">401 Expired</div>
                    <ArrowRight className="h-4 w-4 rotate-90 sm:rotate-0" />
                    <div className="px-3 py-2 bg-primary/10 border border-primary/20 rounded-lg font-mono text-primary">Renew URL</div>
                    <ArrowRight className="h-4 w-4 rotate-90 sm:rotate-0" />
                    <div className="px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg font-mono text-green-600">New Token</div>
                  </div>
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">401 response format</h2>
                  <p className="text-sm text-muted-foreground">
                    When a token is expired, the API responds with:
                  </p>
                  <CodeBlock
                    code={`{
  "error": "Token expired",
  "reauth_url": "https://oovault.nl/auth/renew?token_id=42&type=secret"
}`}
                    language="json"
                  />
                  <p className="text-sm text-muted-foreground">
                    The <code className="bg-muted px-1.5 py-0.5 rounded text-xs">reauth_url</code>{" "}
                    points to a page where the user can log in and generate a replacement token with
                    one click.
                  </p>
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Python example</h2>
                  <p className="text-sm text-muted-foreground">
                    A minimal script that handles token expiry and prompts the user to re-authenticate:
                  </p>
                  <CodeBlock
                    code={`import requests
from pathlib import Path

BASE_URL = "https://oovault.nl"
TOKEN_FILE = Path.home() / ".oovault_token"


def load_token():
    if TOKEN_FILE.exists():
        return TOKEN_FILE.read_text().strip() or None
    return None


def save_token(token):
    TOKEN_FILE.write_text(token)


def get_secret(token):
    r = requests.get(
        f"{BASE_URL}/api/secret",
        headers={"Authorization": f"Bearer {token}"},
        timeout=10,
    )

    if r.status_code == 401:
        data = r.json()
        reauth_url = data.get("reauth_url", "https://oovault.nl")
        raise TokenExpiredError(reauth_url)

    r.raise_for_status()
    return r.json()["value"]


class TokenExpiredError(Exception):
    def __init__(self, reauth_url):
        self.reauth_url = reauth_url


token = load_token() or input("Paste your token: ").strip()

while True:
    try:
        secret = get_secret(token)
        print(f"Secret: {secret}")
        save_token(token)
        break
    except TokenExpiredError as e:
        print(f"\\nToken expired. Renew here: {e.reauth_url}\\n")
        token = input("Paste your new token: ").strip()`}
                    language="python"
                  />
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">cURL example</h2>
                  <CodeBlock
                    code={`# Fetch a secret — on 401 the response includes reauth_url
curl -s -w "\\n%{http_code}" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  https://oovault.nl/api/secret

# Example 401 response:
# {"error":"Token expired","reauth_url":"https://oovault.nl/auth/renew?token_id=42&type=secret"}`}
                    language="bash"
                  />
                </section>

                <section className="space-y-4">
                  <h2 className="text-base font-semibold">Status code reference</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 pr-4 font-medium">Status</th>
                          <th className="text-left py-2 pr-4 font-medium">Meaning</th>
                          <th className="text-left py-2 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody className="text-muted-foreground">
                        {[
                          { code: "200", meaning: "Success", action: "Use the returned value" },
                          { code: "401", meaning: "Token expired", action: <>Visit <code className="bg-muted px-1.5 py-0.5 rounded text-xs">reauth_url</code> to get a new token</> },
                          { code: "403", meaning: "Forbidden (revoked, IP blocked)", action: "Check secret status or IP whitelist" },
                          { code: "404", meaning: "Invalid token", action: "Token does not exist — generate a new one from the UI" },
                          { code: "429", meaning: "Rate limited", action: "Back off and retry, or upgrade your plan" },
                        ].map(({ code, meaning, action }) => (
                          <tr key={code} className="border-b last:border-0">
                            <td className="py-2 pr-4">
                              <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{code}</code>
                            </td>
                            <td className="py-2 pr-4">{meaning}</td>
                            <td className="py-2">{action}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            )}

            {/* ── Claude Desktop ── */}
            {activeSection === "claude-desktop" && (
              <div className="space-y-10">
                <div className="pb-6 border-b">
                  <h1 className="text-2xl font-bold mb-2">Claude Desktop</h1>
                  <p className="text-muted-foreground">
                    Connect Double-O to Claude Desktop via MCP in under a minute.
                  </p>
                </div>

                <StepCard
                  step={1}
                  title="Get your MCP token"
                  description="Log in to Double-O, go to Settings → MCP Access, and copy your personal MCP bearer token."
                >
                  <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg text-sm">
                    <Key className="h-4 w-4 text-primary flex-shrink-0" />
                    <span>The token is scoped to MCP — it cannot read secrets directly.</span>
                  </div>
                </StepCard>

                <StepCard
                  step={2}
                  title="Add the MCP server to Claude Desktop"
                  description="Open your Claude Desktop config file (~/Library/Application Support/Claude/claude_desktop_config.json on macOS) and add the following:"
                >
                  <CodeBlock
                    code={`{
  "mcpServers": {
    "double-o": {
      "type": "http",
      "url": "https://oovault.nl/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_MCP_TOKEN"
      }
    }
  }
}`}
                    language="json"
                  />
                </StepCard>

                <StepCard
                  step={3}
                  title="Restart Claude Desktop"
                  description="Quit and reopen Claude Desktop. Double-O will appear as an available MCP tool — you can now ask Claude to fetch your secrets by name."
                >
                  <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="text-green-700 dark:text-green-400">
                      Try: "Fetch my OPENAI_API_KEY secret from Double-O"
                    </span>
                  </div>
                </StepCard>
              </div>
            )}

            {/* ── Claude Code ── */}
            {activeSection === "claude-code" && (
              <div className="space-y-10">
                <div className="pb-6 border-b">
                  <h1 className="text-2xl font-bold mb-2">Claude Code</h1>
                  <p className="text-muted-foreground">
                    Use Double-O secrets inside Claude Code via MCP.
                  </p>
                </div>

                <StepCard
                  step={1}
                  title="Get your MCP token"
                  description="Log in to Double-O, go to Settings → MCP Access, and copy your personal MCP bearer token."
                >
                  <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg text-sm">
                    <Key className="h-4 w-4 text-primary flex-shrink-0" />
                    <span>The token is scoped to MCP — it cannot read secrets directly.</span>
                  </div>
                </StepCard>

                <StepCard
                  step={2}
                  title="Add the MCP server"
                  description="Run this command in your terminal, or add a .mcp.json file to your project root:"
                >
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-muted-foreground">Option A — CLI</p>
                    <CodeBlock
                      code={`claude mcp add double-o https://oovault.nl/mcp \\
  --header "Authorization: Bearer YOUR_MCP_TOKEN"`}
                      language="bash"
                    />
                    <p className="text-xs font-medium text-muted-foreground mt-4">Option B — .mcp.json</p>
                    <CodeBlock
                      code={`{
  "mcpServers": {
    "double-o": {
      "type": "http",
      "url": "https://oovault.nl/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_MCP_TOKEN"
      }
    }
  }
}`}
                      language="json"
                    />
                  </div>
                </StepCard>

                <StepCard
                  step={3}
                  title="Use your secrets in Claude Code"
                  description="Claude Code can now fetch secrets on your behalf during coding sessions — no API keys in your codebase."
                >
                  <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="text-green-700 dark:text-green-400">
                      Try: "Use my STRIPE_SECRET_KEY from Double-O to call the Stripe API"
                    </span>
                  </div>
                </StepCard>
              </div>
            )}

            {/* ── Cursor ── */}
            {activeSection === "cursor" && (
              <div className="space-y-10">
                <div className="pb-6 border-b">
                  <h1 className="text-2xl font-bold mb-2">Cursor</h1>
                  <p className="text-muted-foreground">
                    Connect Double-O to Cursor via MCP for secret-aware AI coding.
                  </p>
                </div>

                <StepCard
                  step={1}
                  title="Get your MCP token"
                  description="Log in to Double-O, go to Settings → MCP Access, and copy your personal MCP bearer token."
                >
                  <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg text-sm">
                    <Key className="h-4 w-4 text-primary flex-shrink-0" />
                    <span>The token is scoped to MCP — it cannot read secrets directly.</span>
                  </div>
                </StepCard>

                <StepCard
                  step={2}
                  title="Add the MCP server in Cursor"
                  description="Open Cursor Settings → Features → MCP, click Add MCP Server, and fill in the details. Or add a .mcp.json to your project root:"
                >
                  <CodeBlock
                    code={`{
  "mcpServers": {
    "double-o": {
      "type": "http",
      "url": "https://oovault.nl/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_MCP_TOKEN"
      }
    }
  }
}`}
                    language="json"
                  />
                </StepCard>

                <StepCard
                  step={3}
                  title="Use your secrets in Cursor's AI chat"
                  description="Open Cursor's AI chat (Cmd+L) and ask it to use your secrets. Cursor will call Double-O to fetch them securely."
                >
                  <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="text-green-700 dark:text-green-400">
                      Try: "Fetch my DATABASE_URL from Double-O and help me write a migration script"
                    </span>
                  </div>
                </StepCard>
              </div>
            )}
          </main>
        </div>
      </div>
    </MarketingLayout>
  );
}
